import { TFolderEntry } from '@/api/queries/project/useGetProjectFolderEntries';
import DraggableFolderEntry from '@/ui/pages/projectPage/components/FilesPanel/DraggableFolderEntry';
import { Badge, EmptyState } from '@payaca/ambrosia';
import FolderEntry from '@payaca/components/folderEntry/FolderEntry';
import { DragOverlay } from '@payaca/dnd';

import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { AnimatePresence, motion } from 'motion/react';
import { FC } from 'react';

export type TProps = {
  projectId: string;
  isFetchingProjectFolderEntries: boolean;
  projectFolderEntries?: TFolderEntry[];
  selectedEntries: TFolderEntry[];
  onViewFolderEntry: (folderEntryId: string) => void;
  selectFolderEntry: (folderEntry: TFolderEntry) => void;
  unselectFolderEntry: (folderEntry: TFolderEntry) => void;
  onDelete: (folderEntry: TFolderEntry) => void;
  onShare: (folderEntry: TFolderEntry) => void;
  onUnshare: (folderEntry: TFolderEntry) => void;
  onRename: (folderEntry: TFolderEntry) => void;
};

export const MULTIPLE_ENTRIES_DRAG_ID = 'multiple-entries-drag';

const FolderEntriesList: FC<TProps> = (props) => {
  const {
    projectId,
    selectedEntries,
    isFetchingProjectFolderEntries,
    projectFolderEntries,
    selectFolderEntry,
    unselectFolderEntry,
    onDelete,
    onShare,
    onUnshare,
    onRename,
    onViewFolderEntry,
  } = props;

  const toggleEntrySelection =
    (folderEntry: TFolderEntry) => (checked: boolean) => {
      if (checked) {
        selectFolderEntry(folderEntry);
      } else {
        unselectFolderEntry(folderEntry);
      }
    };

  const isAnyEntrySelected = selectedEntries.length > 0;

  const isEmpty = projectFolderEntries?.length === 0;
  return (
    <>
      <div
        className={clstx(
          // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Auto-fill grid, so the column count follows the card width
          'grid gap-4 grid-cols-[repeat(auto-fill,minmax(165px,1fr))]'
        )}
      >
        <AnimatePresence>
          {projectFolderEntries?.map((folderEntry) => {
            const isEntrySelected = selectedEntries.some(
              (i) => i.id === folderEntry.id
            );
            const isSelectable = folderEntry.isShareable.result;

            return (
              <DraggableFolderEntry
                key={folderEntry.id}
                projectId={projectId}
                idOverride={
                  isAnyEntrySelected ? MULTIPLE_ENTRIES_DRAG_ID : undefined
                }
                folderEntry={folderEntry}
                disabled={isAnyEntrySelected && !isEntrySelected}
                hovered={isAnyEntrySelected}
                selected={selectedEntries.some((i) => i.id === folderEntry.id)}
                checkbox={isSelectable}
                onClick={() => {
                  if (isAnyEntrySelected) {
                    if (isSelectable) {
                      toggleEntrySelection(folderEntry)(!isEntrySelected);
                    }
                  } else {
                    onViewFolderEntry(folderEntry.id);
                  }
                }}
                onSelectChange={
                  isSelectable ? toggleEntrySelection(folderEntry) : undefined
                }
                onDelete={() => onDelete(folderEntry)}
                onShare={() => onShare(folderEntry)}
                onUnshare={() => onUnshare(folderEntry)}
                onRename={() => onRename(folderEntry)}
              />
            );
          })}
          {!projectFolderEntries && isFetchingProjectFolderEntries && (
            <>
              {[1, 2, 3, 4].map((i) => (
                <motion.div
                  key={i}
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                    transition: { delay: 0.5 + i * 0.1 },
                  }}
                  exit={{ opacity: 0, transition: { delay: 0 } }}
                >
                  <FolderEntry.Loading />
                </motion.div>
              ))}
            </>
          )}
        </AnimatePresence>
      </div>

      <DragOverlay>
        <div className="flex items-center justify-center">
          <UntitledIcon className="h-12 w-12 fill-white" name="file-06.3" />
          <Badge variant="solid" colour="blue">
            {isAnyEntrySelected ? selectedEntries.length : 1}
          </Badge>
        </div>
      </DragOverlay>

      {isEmpty && (
        <EmptyState
          className="pt-10 pb-9"
          text="No files found in this folder"
        />
      )}
    </>
  );
};

export default FolderEntriesList;
